import { Injectable } from "@angular/core";
import { StoreService } from "..";
import { catchError, map, Observable, tap } from "rxjs";
import { CategorizationApiService, Categorizations } from "../../services/api/categorization-api.service";
import { BuildingBlockPurpose } from "../../models/building-blocks/building-block-purpose.model";
import { BuildingBlockSubType } from "../../models/building-blocks/building-block-sub-type.model";
import { BuildingBlockBinauralBeatsSoundType } from "../../models/building-blocks/building-block-binaural-beats-sound-type.model";
import { BuildingBlockBinauralBeatsFrequencyVariation } from "../../models/building-blocks/building-block-binaural-beats-frequency-variation.model";
import { BuildingBlockCategory } from "../../models/building-blocks/building-block-category.model";
import { TimeSlot } from "../../models/time-slot.model";
import { ProgramTemplateSessionFrequency } from "../../models/programs/program-template-session-frequency.model";
import { ProgramTemplateIntensityLevel } from "../../models/programs/program-template-intensity-level.model";

@Injectable({
  providedIn: 'root'
})
export class CategorizationService {

  constructor(
    private store: StoreService, 
    private categorizationApiService: CategorizationApiService
  ) {}

  get buildingBlockCategories$(): Observable<BuildingBlockCategory[]> {
    return this.store.state$.pipe(map(state => Object.values(state.buildingBlockCategories)));
  }

  get buildingBlockPurposes$(): Observable<BuildingBlockPurpose[]> {
    return this.store.state$.pipe(map(state => Object.values(state.buildingBlockPurposes)));
  }

  get buildingBlockSubTypes$(): Observable<BuildingBlockSubType[]> {
    return this.store.state$.pipe(map(state => Object.values(state.buildingBlockSubTypes)));
  }

  get binauralBeatsFrequencyVariations$(): Observable<BuildingBlockBinauralBeatsFrequencyVariation[]> {
    return this.store.state$.pipe(map(state => Object.values(state.buildingBlockBinauralBeatsFrequencyVariations)));
  }

  get binauralBeatsSoundTypes$(): Observable<BuildingBlockBinauralBeatsSoundType[]> {
    return this.store.state$.pipe(map(state => Object.values(state.buildingBlockBinauralBeatsSoundTypes)));
  }

  get programTemplateIntensityLevels$(): Observable<ProgramTemplateIntensityLevel[]> {
    return this.store.state$.pipe(map(state => Object.values(state.programTemplateIntensityLevels)));
  }

  get programTemplateSessionFrequencies$(): Observable<ProgramTemplateSessionFrequency[]> {
    return this.store.state$.pipe(map(state => Object.values(state.programTemplateSessionFrequencies)));
  }

  get timeSlots$(): Observable<TimeSlot[]> {
    return this.store.state$.pipe(map(state => Object.values(state.timeSlots)));
  }

  load(): Observable<Categorizations> {
    this.store.setState({ ...this.store.state, loading: true, error: null });
    return this.categorizationApiService.getCategorizations().pipe(
      tap(({ 
        buildingBlockPurposes,
        buildingBlockSubTypes, 
        buildingBlockBinauralBeatsFrequencyVariations, 
        buildingBlockBinauralBeatsSoundTypes,
        buildingBlockCategories,
        programTemplateIntensityLevels,
        programTemplateSessionFrequencies,
        timeSlots
      }) => {
        const state = { ...this.store.state };
        buildingBlockPurposes.forEach((buildingBlockPurpose) => state.buildingBlockPurposes[buildingBlockPurpose.id] = buildingBlockPurpose);
        buildingBlockPurposes.forEach((buildingBlockPurpose) => state.buildingBlockPurposeCodes[buildingBlockPurpose.code] = buildingBlockPurpose.id);

        buildingBlockSubTypes.forEach((buildingBlockSubType) => state.buildingBlockSubTypes[buildingBlockSubType.id] = buildingBlockSubType);
        
        buildingBlockBinauralBeatsFrequencyVariations.forEach((buildingBlockBinauralBeatsFrequencyVariation) => state.buildingBlockBinauralBeatsFrequencyVariations[buildingBlockBinauralBeatsFrequencyVariation.id] = buildingBlockBinauralBeatsFrequencyVariation);
        buildingBlockBinauralBeatsFrequencyVariations.forEach((buildingBlockBinauralBeatsFrequencyVariation) => state.buildingBlockBinauralBeatsFrequencyVariationCodes[buildingBlockBinauralBeatsFrequencyVariation.code] = buildingBlockBinauralBeatsFrequencyVariation.id);
        
        buildingBlockBinauralBeatsSoundTypes.forEach((buildingBlockBinauralBeatsSoundType) => state.buildingBlockBinauralBeatsSoundTypes[buildingBlockBinauralBeatsSoundType.id] = buildingBlockBinauralBeatsSoundType);
        buildingBlockBinauralBeatsSoundTypes.forEach((buildingBlockBinauralBeatsSoundType) => state.buildingBlockBinauralBeatsSoundTypeCodes[buildingBlockBinauralBeatsSoundType.code] = buildingBlockBinauralBeatsSoundType.id);
        
        buildingBlockCategories.forEach((buildingBlockCategory) => state.buildingBlockCategories[buildingBlockCategory.id] = buildingBlockCategory);
        buildingBlockCategories.forEach((buildingBlockCategory) => state.buildingBlockCategoryCodes[buildingBlockCategory.code] = buildingBlockCategory.id);
        
        programTemplateIntensityLevels.forEach((programTemplateIntensityLevel) => state.programTemplateIntensityLevels[programTemplateIntensityLevel.id] = programTemplateIntensityLevel);
        programTemplateSessionFrequencies.forEach((programTemplateSessionFrequency) => state.programTemplateSessionFrequencies[programTemplateSessionFrequency.id] = programTemplateSessionFrequency);
        timeSlots.forEach((timeSlot) => state.timeSlots[timeSlot.id] = timeSlot);
        state.loading = false;
        this.store.setState(state);
      }),
      catchError((error) => {
        this.store.setState({ ...this.store.state, loading: false, error });
        console.error('Error loading categorizations:', error);
        throw error;
      })
    );
  }

  clear(): void {
    const state = { ...this.store.state };
    state.buildingBlockCategories = {};
    state.buildingBlockCategoryCodes = {};
    state.buildingBlockPurposes = {};
    state.buildingBlockPurposeCodes = {};
    state.buildingBlockSubTypes = {};
    state.buildingBlockBinauralBeatsFrequencyVariations = {};
    state.buildingBlockBinauralBeatsFrequencyVariationCodes = {};
    state.buildingBlockBinauralBeatsSoundTypes = {}; 
    state.buildingBlockBinauralBeatsSoundTypeCodes = {};
    state.programTemplateIntensityLevels = {};
    state.programTemplateSessionFrequencies = {};
    state.timeSlots = {};
    this.store.setState(state);
  }
}
